import {
  Box,
  List,
  Skeleton,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  ContractIcon,
  QualifyIcon,
  VerifiedUserIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import ProgressStatus from 'components/Atoms/ProgressStatus';
import ContactUs from 'components/Organisms/ContactUs';
import ListItemProgressBar from 'components/Organisms/ListItemProgressBar';
import PATHS from 'constants/paths';
import { UserContext } from 'contexts/UserContext';
import useBaseURL from 'hooks/useBaseURL';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useAffordaDashboard from './hook';
import Banners from './Banners';
import SectionDivider from 'components/Atoms/SectionDivider';

const Dashboard = () => {
  const intl = useIntl();
  const {
    userProfile: { firstName },
  } = useContext(UserContext);
  const baseURL = useBaseURL();

  const {
    isDataLoaded,
    applicationProps,
    creditAndCriminalProps,
    isStudentDisqualified,
    documentationProps,
    isIncomeAssetsNoEligible,
    isApplicationDenied,
    isWelcomeAndQualifyUneligible,
  } = useAffordaDashboard();

  const isAllSectionsCompleted = useMemo(() => {
    return (
      applicationProps.isComplete &&
      creditAndCriminalProps.isComplete &&
      documentationProps.isComplete
    );
  }, [applicationProps, creditAndCriminalProps, documentationProps]);

  return (
    <Box>
      {/* Completed all sections, whats next? */}
      {isAllSectionsCompleted && (
        <Typography
          component="div"
          align="center"
          variant="bodyLarge"
          marginY={0}
          paddingX={8}
        >
          <FormattedMessage id="afforda.dashboard.thanks" />
        </Typography>
      )}

      <SectionDivider />

      <Typography
        component={'div'}
        align={'center'}
        color={'text.secondary'}
        variant={'bodyLarge'}
        marginY={isAllSectionsCompleted ? 1 : 3}
        marginX={'auto'}
        width={'100%'}
        loading={!firstName?.length}
      >
        <FormattedMessage
          id="afforda.dashboard.title"
          values={{ name: firstName }}
        />
      </Typography>

      {/* Completed all sections, whats next? */}
      {isAllSectionsCompleted && (
        <>
          <Typography
            component="div"
            align="center"
            variant="bodySmall"
            marginY={0}
          >
            <FormattedMessage id="afforda.dashboard.isInCompliance" />
          </Typography>
          <Typography
            component="div"
            align="center"
            variant="bodySmall"
            marginY={1}
            marginBottom={3}
          >
            <FormattedMessage id="afforda.dashboard.weWillContactYou" />
          </Typography>
        </>
      )}

      <SectionDivider />
      {documentationProps.isAwaitingDocumentation && !isApplicationDenied && (
        <>
          <Typography
            component="div"
            align="center"
            variant="bodySmall"
            marginY={3}
          >
            <FormattedMessage id="afforda.documentation.awaitingDocumentation" />
          </Typography>
        </>
      )}
      {documentationProps.allMembersHaveCompletedDocumentation &&
        !isApplicationDenied && (
          <>
            <Typography
              component="div"
              align="center"
              variant="bodySmall"
              width="100%"
            >
              <FormattedMessage id="afforda.documentation.applicationUnderReview" />
            </Typography>
          </>
        )}

      <Banners
        creditAndCriminalScreeningIneligible={
          creditAndCriminalProps.creditAndCriminalScreeningIneligible
        }
        isApplicationDenied={isApplicationDenied}
        isIncomeAssetsNoEligible={isIncomeAssetsNoEligible}
        isStudentDisqualified={isStudentDisqualified}
        isWelcomeAndQualifyUneligible={isWelcomeAndQualifyUneligible}
      />

      <AsyncBox
        loading={!isDataLoaded}
        Skeleton={
          <List sx={{ paddingTop: 0 }}>
            {[PATHS.APPLICATION, PATHS.VERIFICATION, PATHS.DOCUMENTATION].map(
              (path) => (
                <Skeleton
                  key={path}
                  height={72}
                  width={'100%'}
                  variant={'rounded'}
                  sx={{ marginBottom: 1, borderRadius: 2 }}
                />
              ),
            )}
          </List>
        }
      >
        <List sx={{ paddingTop: 0 }}>
          <ListItemProgressBar
            primaryText={
              <FormattedMessage id="afforda.dashboard.qualification" />
            }
            to={`${baseURL}/${PATHS.APPLICATION}`}
            icon={<QualifyIcon fontSize="large" />}
            progressValue={applicationProps.progress}
            secondary={
              isStudentDisqualified ? (
                <ProgressStatus
                  disabled={applicationProps.disabled}
                  intl={intl}
                  variant={'submitted'}
                />
              ) : applicationProps.isComplete ? (
                <ProgressStatus
                  disabled={applicationProps.disabled}
                  intl={intl}
                  variant={'completed'}
                />
              ) : null
            }
            disabled={applicationProps.disabled || isApplicationDenied}
          />
          <ListItemProgressBar
            primaryText={
              <FormattedMessage id="afforda.dashboard.verification" />
            }
            to={`${baseURL}/${PATHS.VERIFICATION}`}
            icon={<VerifiedUserIcon fontSize="large" />}
            progressValue={creditAndCriminalProps.progress}
            secondary={
              creditAndCriminalProps.isAwaitingScreeningResult ? (
                <ProgressStatus
                  disabled={creditAndCriminalProps.disabled}
                  intl={intl}
                  variant={'submitted'}
                />
              ) : creditAndCriminalProps.isComplete ? (
                <ProgressStatus
                  disabled={creditAndCriminalProps.disabled}
                  intl={intl}
                  variant={'completed'}
                />
              ) : null
            }
            disabled={creditAndCriminalProps.disabled || isApplicationDenied}
          />
          <ListItemProgressBar
            primaryText={
              <FormattedMessage id="afforda.dashboard.documentation" />
            }
            to={`${baseURL}/${PATHS.DOCUMENTATION}`}
            icon={<ContractIcon fontSize="large" />}
            progressValue={documentationProps.progress}
            secondary={
              documentationProps.isComplete ? (
                <ProgressStatus
                  disabled={documentationProps.disabled}
                  intl={intl}
                  variant={'completed'}
                />
              ) : null
            }
            disabled={documentationProps.disabled || isApplicationDenied}
          />
        </List>
      </AsyncBox>

      <SectionDivider />

      <ContactUs />
    </Box>
  );
};

export default Dashboard;
